<template>
  <v-container fluid class="pa-0">
    <ValidationObserver ref="validateObserver" v-slot="{ invalid }">
      <v-row align="center" justify="center">
        <v-col>
          <form autocomplete="off">
            <!-- Name/CEP/Correspondence -->
            <v-row dense align="center" justify="center">
              <v-col>
                <mb-input-text v-model="address.label"
                  name="adrs_label" label="Nome do Local" rules="required"
                />
              </v-col>
              <v-col xl="4" lg="4" md="4" sm="4" cols="12">
                <mb-input-cep v-model="address.zip" name="adrs_cep"
                  rules="required|cep" @gettedByCep="gettedByCep"/>
              </v-col>
              <v-col xl="3" lg="3" md="3" sm="3" cols="12">
                <v-switch id="adrs_correspondence" v-model="address.correspondence"
                  label="Correspondência" class="pa-0 ma-0 pl-2"
                />
              </v-col>
            </v-row>
            <!-- Street/Number -->
            <v-row dense>
              <v-col>
                <mb-input-text v-model="address.street" :disabled="!address.zip || loadingGetByCep"
                  name="adrs_street" label="Logradouro" rules="required"
                />
              </v-col>
              <v-col xl="4" lg="4" md="4" sm="4" cols="12">
                <mb-input-text v-model="address.number" name="adrs_number"
                  label="Número" rules="required"
                />
              </v-col>
            </v-row>
            <!-- Complement/District -->
            <v-row dense>
              <v-col xl="6" lg="6" md="6" sm="6" cols="12">
                <mb-input-text v-model="address.complement" name="adrs_complement"
                  label="Complemento" />
              </v-col>
              <v-col xl="6" lg="6" md="6" sm="6" cols="12">
                <mb-input-text v-model="address.district"
                  :disabled="!address.zip || loadingGetByCep"
                  name="adrs_district" label="Bairro" rules="required"
                />
              </v-col>
            </v-row>
            <!-- City/State -->
            <v-row dense>
              <v-col>
                <mb-input-text v-model="address.city" name="adrs_city" label="Cidade"
                  rules="required" :disabled="!address.zip || loadingGetByCep" />
              </v-col>
              <v-col xl="3" lg="3" md="3" sm="3" cols="12">
                <mb-select v-model="address.state" :items="states" name="adrs_state"
                  label="UF" :disabled="!address.zip || loadingGetByCep" />
              </v-col>
            </v-row>
            <!-- Buttons -->
            <v-row dense>
              <v-col>
                <mb-text-button name="adrs_cancel" label="Cancelar"
                  @click="cancel" :primary="false" />
              </v-col>
              <v-col class="pb-5" align="right"
                xl="4" lg="4" md="4" sm="4" cols="12"
                :order="($vuetify.breakpoint.xsOnly) ? 'first' : 'last'"
              >
                <mb-text-button name="adrs_save" label="Salvar"
                  :disabled="invalid"
                  :loading="saveBtnLoading"
                  @click="save"
                />
              </v-col>
            </v-row>
          </form>
        </v-col>
      </v-row>
    </ValidationObserver>
  </v-container>
</template>

<script>
import { ValidationObserver } from 'vee-validate';
import bus from '@/plugins/eventBus';
import MbInputText from '@/components/general/inputs/MbInputText';
import MbInputCep from '@/components/general/inputs/MbInputCep';
import MbSelect from '@/components/general/inputs/MbSelect';
import MbTextButton from '@/components/general/buttons/MbTextButton';

export default {
  props: {
    value: {
      type: Object,
    },
  },
  data: () => ({
    address: {
      id: null,
      patientId: null,
      label: null,
      zip: null,
      state: null,
      city: null,
      district: null,
      street: null,
      number: null,
      complement: null,
      latitude: null,
      longitude: null,
      correspondence: false,
    },
    states: [
      'AC',
      'AL',
      'AP',
      'AM',
      'BA',
      'CE',
      'DF',
      'ES',
      'GO',
      'MA',
      'MT',
      'MS',
      'MG',
      'PA',
      'PB',
      'PR',
      'PE',
      'PI',
      'RJ',
      'RN',
      'RS',
      'RO',
      'RR',
      'SC',
      'SP',
      'SE',
      'TO',
    ],
    correspondenceOptions: [
      { text: 'Sim', vl: true },
      { text: 'Não', vl: false },
    ],
    saveBtnLoading: false,
    loadingGetByCep: false,
  }),
  computed: {
    user() {
      return this.$store.getters['auth/getUser'];
    },
  },
  created() {
    if (this.value) {
      Object.keys(this.value)
        .filter(key => key in this.address)
        .forEach((key) => {
          this.address[key] = this.value[key];
        });

      this.address.id = this.value.id;
    }

    this.address.patientId = this.user.id;
  },
  methods: {
    save() {
      // first, get latitude and longitude
      this.getGeolocation()
        .finally(() => {
          // if id then update, else save
          if (this.address.id) {
            this.update();
          } else {
            this.create();
          }
        });
    },
    async create() {
      const isValid = await this.$refs.validateObserver.validate();
      if (isValid) {
        this.saveBtnLoading = true;

        const newsAddress = this.address;
        delete newsAddress.id;

        this.$store
          .dispatch('patientAddresses/create', newsAddress)
          .then((addressSaved) => {
            this.address.id = addressSaved.id;
            this.$emit('input', this.s);
            bus.$emit('message', {
              message: 'Endereço criado com sucesso!',
              type: 'success',
            });
          })
          .catch((err) => {
            bus.$emit('message', {
              message: err.response.data.message,
              type: 'error',
            });
          })
          .finally(() => {
            this.saveBtnLoading = false;
          });
      }
    },
    async update() {
      const isValid = await this.$refs.validateObserver.validate();
      if (isValid) {
        this.saveBtnLoading = true;

        this.$store
          .dispatch('patientAddresses/update', this.address)
          .then(() => {
            bus.$emit('message', {
              message: 'Endereço atualizado com sucesso!',
              type: 'success',
            });
          })
          .catch((err) => {
            bus.$emit('message', {
              message: err.response.data.message,
              type: 'error',
            });
          })
          .finally(() => {
            this.saveBtnLoading = false;
          });
      }
    },
    gettedByCep(obj) {
      this.address = { ...this.address, ...obj };
    },
    getGeolocation() {
      const query = encodeURI(`${this.address.street} ${(this.address.district) ? this.address.district : ''} ${this.address.city}`);
      const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${query}&key=${process.env.VUE_APP_GOOGLE_API}`;

      return fetch(url)
        .then((res) => {
          if (res.status === 200) {
            return res.json().then((data) => {
              if (data.results && data.results.length > 0) {
                this.address.latitude = `${data.results[0].geometry.location.lat}`;
                this.address.longitude = `${data.results[0].geometry.location.lng}`;
              }
            });
          }
          return null;
        });
    },
    cancel() {
      this.$router.push('/addresses/list');
    },
  },
  watch: {
    value(vl) {
      if (vl) {
        Object.keys(vl)
          .filter(key => key in this.address)
          .forEach((key) => {
            this.address[key] = vl[key];
          });
      }
    },
  },
  components: {
    ValidationObserver,
    MbInputText,
    MbInputCep,
    MbSelect,
    MbTextButton,
  },
};
</script>
